/**
 * Statement import — client side. The files go to OUR backend (/rest/ai/extract-assets), which holds
 * the AI key and does the reading; the browser never talks to a provider. Nothing is stored anywhere:
 * the drafts come back for the adviser to tick.
 */
import axios from 'axios';
import type { AxiosError } from 'axios';
import { getAccessToken } from './authClient.ts';
import { getStoredProfileId } from './profileApi.ts';
import { apiBaseUrl } from './apiBase.ts';
import { assetsForDuplicateCheck, type StatementImportDraft, type ImportedLiabilityDraft } from '../../../shared/src/calculation/statementImport.ts';

export interface StatementImportResult {
    assets: StatementImportDraft[];
    liabilities: ImportedLiabilityDraft[];
}

export interface ExtractAssetsInput {
    files: File[];
    household: { isCouple: boolean; clientName?: string | null; partnerName?: string | null };
    existingAssets?: Record<string, unknown>;
    /** The home as recorded on Living Arrangements, so an imported value is offered only where it is blank. */
    existingHome?: { value?: unknown } | null;
}

export class StatementImportError extends Error {
    code: string;
    constructor(code: string, message: string) {
        super(message);
        this.code = code;
    }
}

const readAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
        reader.onload = () => {
            const result = String(reader.result || '');
            resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
        };
        reader.readAsDataURL(file);
    });

/** What the adviser reads when a read fails — the code, in words. */
export function statementImportFailureForUser(err: unknown): string {
    const axiosErr = err as AxiosError<{ error?: { code?: string; message?: string } }>;
    const code = axiosErr.response?.data?.error?.code;
    const message = axiosErr.response?.data?.error?.message;
    if (code === 'AI_QUOTA_EXCEEDED') return "You've used today's statement-reading allowance. It resets tomorrow — you can still add assets by hand.";
    if (code === 'UNSUPPORTED_FILE' || code === 'FILE_TOO_LARGE' || code === 'FILES_TOO_LARGE') return message || 'That file could not be used.';
    if (code === 'NOT_CONFIGURED' || axiosErr.response?.status === 503) return 'Statement reading is not available right now. You can still add assets by hand.';
    if (axiosErr.response?.status === 401) return 'Please sign in to import a statement.';
    // No response at all: either the read outran the wait, or the connection dropped (a restarting
    // server mid-read looks like this too).
    if (!axiosErr.response) {
        return axiosErr.code === 'ECONNABORTED'
            ? 'That took longer than four minutes to read. Try fewer pages or files at a time.'
            : "Couldn't reach the server — the connection dropped before the reply came back. Try again.";
    }
    if (axiosErr.response.status === 429) return 'Too many reads in a row — wait a minute and try again.';
    // A gateway giving up on a long request (the front door's own limit), not the reader.
    if ([502, 504, 524].includes(axiosErr.response.status) || (axiosErr.response.status === 503 && !code)) {
        return 'That document took too long to read over this connection — try fewer pages or files at a time.';
    }
    // Say what the server said, so a failure is never a mystery: Fastify's own validation errors carry
    // `message` at the top level, ours under `error`.
    const detail = message || (axiosErr.response.data as { message?: string } | undefined)?.message;
    const codeNote = code ? ` (${code})` : '';
    return detail ? `Couldn't read that statement: ${detail}${codeNote}` : `Couldn't read that statement${codeNote}. Try a clearer copy, or add the assets by hand.`;
}

/** How often the browser asks after a read, and how long it keeps asking. */
export const POLL_EVERY_MS = 2_500;
export const POLL_FOR_MS = 5 * 60 * 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A failed read, shaped like the request error the message writer already reads. */
const failedRead = (httpStatus: number, error: { code?: string; message?: string }) => ({ response: { status: httpStatus, data: { error } } });

/**
 * The read runs on the server in the background: the documents go up, a ticket comes back at once,
 * and the browser asks after the ticket every few seconds until the rows are ready (owner, 16 Oct
 * 2026 — a one-minute read was cut off by production's front door). No single request lasts long.
 */
export async function extractAssetsFromStatements(input: ExtractAssetsInput): Promise<StatementImportResult> {
    const files = await Promise.all(
        input.files.map(async (file) => ({ name: file.name, mimeType: file.type || 'application/octet-stream', dataBase64: await readAsBase64(file) })),
    );
    const token = await getAccessToken().catch(() => null);
    const headers: { 'Content-Type': string; Authorization?: string } = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const profileId = getStoredProfileId();
    const started = await axios.post(
        `${apiBaseUrl()}/rest/ai/extract-assets`,
        {
            files,
            household: {
                isCouple: input.household.isCouple,
                ...(input.household.clientName ? { clientName: input.household.clientName } : {}),
                ...(input.household.partnerName ? { partnerName: input.household.partnerName } : {}),
            },
            ...(profileId ? { profileId } : {}),
            // Names and values only: an asset's id is a raw UUID, which the server's id gate refuses.
            ...(input.existingAssets || input.existingHome ? { existingAssets: assetsForDuplicateCheck(input.existingAssets, input.existingHome) } : {}),
        },
        { headers, timeout: 60_000 },
    );
    const jobId = String(started.data?.jobId || '');
    if (!jobId) throw failedRead(502, { code: 'AI_ERROR', message: 'The read did not start.' });

    const deadline = Date.now() + POLL_FOR_MS;
    while (Date.now() < deadline) {
        await wait(POLL_EVERY_MS);
        const asked = await axios.get(`${apiBaseUrl()}/rest/ai/extract-assets/${encodeURIComponent(jobId)}`, { headers, timeout: 30_000 });
        const data = asked.data as { status?: string; drafts?: unknown; liabilities?: unknown; error?: { code?: string; message?: string; httpStatus?: number } };
        if (data.status === 'done') {
            return {
                assets: Array.isArray(data.drafts) ? (data.drafts as StatementImportDraft[]) : [],
                liabilities: Array.isArray(data.liabilities) ? (data.liabilities as ImportedLiabilityDraft[]) : [],
            };
        }
        if (data.status === 'failed') throw failedRead(data.error?.httpStatus ?? 502, data.error ?? {});
    }
    throw { code: 'ECONNABORTED' };
}
